import { ApexChartWidget } from './apex-chart-widget';
import { ChartDataXY, ChartSeriesXY } from '../data/chart-data-types';
import { customElement, property } from 'lit/decorators.js';
import { generatedPurchaseOrdersCache } from '../data/generated-purchase-orders-cache';
import { lang, tlang } from '../../../../webmodule-common/other/language/lang';
import { localDateTimeToServer } from '../../../../webmodule-common/other/datetime-converter';
import { NullPromise } from '../../../../webmodule-common/interop/webmodule-interop';
import { PurchaseOrderState } from '../../common/object-states';
import { ResultGetGeneratedPurchaseOrderRevenue } from '../../../api/supplier-api-interface-franchiseenetwork';

@customElement('generated-purchase-orders-revenue-widget')
export class GeneratedPurchaseOrdersWidget extends ApexChartWidget {
  @property({ type: Array }) branchIds?: string[] = [];
  @property() userId?: string;
  @property({ type: Array }) states: PurchaseOrderState[] = [
    PurchaseOrderState.Draft,
    PurchaseOrderState.IssuedPending,
    PurchaseOrderState.Issued,
    PurchaseOrderState.Completed
  ];

  private _cache = generatedPurchaseOrdersCache();

  constructor() {
    super('purchase-order-issued-revenue');
  }

  protected async refreshData(): NullPromise<ResultGetGeneratedPurchaseOrderRevenue> {
    if (this.dateRange && this.branchIds) {
      const startDate = localDateTimeToServer(this.dateRange.startDate);
      const endDate = localDateTimeToServer(this.dateRange.endDate);
      const result = await this._cache?.getData(startDate, endDate, this.states, this.branchIds, this.userId);
      if (result) {
        return result;
      }
    }
    return null;
  }

  protected override getTitle(): string {
    return tlang`!!purchase-order!! (Net Amount) - ${this.dateRange?.label}`;
  }

  protected getSeriesColorByState(state: PurchaseOrderState): string {
    // return the corresponding badge colour
    switch (state) {
      case PurchaseOrderState.Draft:
        return '#0ea5e9';
      case PurchaseOrderState.IssuedPending:
        return '#a855f7';
      case PurchaseOrderState.Issued:
        return '#eab308';
      case PurchaseOrderState.Completed:
        return '#52525b';
      default:
        return '#3282cc'; // base colour
    }
  }

  protected updateData(): void {
    this.refreshData().then(results => {
      if (results) {
        const chartSeries: ChartSeriesXY[] = [];

        for (const purchaseOrderState of results.purchaseOrderStates) {
          const chartData: ChartDataXY[] = [];
          const data = results.revenueSummary.filter(d => d.state === purchaseOrderState || d.state === -1);
          data.map(r =>
            chartData.push({
              x: this.convertDateToMillis(r.date),
              y: r.dailyRevenue
            })
          );
          chartSeries.push({
            name: tlang`${getPurchaseOrderStatusStr(purchaseOrderState)}`,
            data: chartData,
            color: this.getSeriesColorByState(purchaseOrderState)
          });
        }
        if (this.chart.series) {
          this.chart.updateSeries(chartSeries, true);
        }
      }
    });
  }
}

function getPurchaseOrderStatusStr(state: PurchaseOrderState): string {
  const stateToString = () => {
    switch (state) {
      case PurchaseOrderState.Completed:
        return 'completed';
      case PurchaseOrderState.Issued:
        return 'issued';
      case PurchaseOrderState.IssuedPending:
        return 'issuedpending';
      case PurchaseOrderState.Draft:
        return 'draft';
      default:
        return 'none';
    }
  };

  const text = `%%purchase-order-state-${stateToString()}%%`;
  return lang(text);
}
